import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ListTree } from 'lucide-react';
import { useState } from 'react';
import { ManagedList, type ManagedField } from '../components/ManagedList';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import type { JobTypeRow, StockLocationRow, SupplierRow, ZoneRow } from '../lib/types';

/**
 * The lists this business runs on, in one place.
 *
 * ## Why they are together
 *
 * Job types, zones, suppliers, and the stores and vans are the reference data
 * every other screen is built from. Scattered — a job type in Settings, a zone
 * on the areas screen, a supplier in Purchasing — they are four screens nobody
 * finds, and in practice they were never curated at all. Half of them had no
 * screen: job types and zones were read-only, which meant a business that
 * started offering surveys had to ask a developer and in the meantime filed
 * them under "New install", where no report could tell them apart.
 *
 * The pickers that *use* these lists stay where they are. This is the place
 * you come to when the list itself is wrong.
 *
 * ## One rule holds across all of them
 *
 * **Nothing is deleted once anything has used it.** A ticket names its job
 * type, an area names its zone, a purchase order names its supplier, and a
 * movement names the van it came off — so a deleted row does not tidy the list,
 * it removes the meaning of every record pointing at it. Retiring takes it out
 * of the pickers, leaves the history exactly as it was, and reverses.
 *
 * The one exception, everywhere: a row that nothing has *ever* used is deleted
 * outright, because somebody who types a name wrongly and notices a minute
 * later should not be left with it retired in the list forever. The server
 * decides which of the two happened and says so; this screen never guesses.
 */
export function ListsPage() {
  const { can } = useSession();
  const queryClient = useQueryClient();

  const [outcome, setOutcome] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const said = (message: string, keys: string[]) => {
    setProblem(null);
    setOutcome(message);
    for (const key of keys) void queryClient.invalidateQueries({ queryKey: [key] });
  };

  const failed = (error: unknown, fallback: string) => {
    setOutcome(null);
    setProblem(error instanceof ApiError ? error.message : fallback);
  };

  /* ── Job types ───────────────────────────────────────────────────── */

  const mayJobTypes = can('settings:write');
  const jobTypes = useQuery({ queryKey: ['job-types-all'], queryFn: api.jobTypesAll });

  const refreshJobTypes = ['job-types-all', 'job-types', 'catalogue'];

  const addJobType = useMutation({
    mutationFn: (draft: Record<string, string>) =>
      api.addJobType({
        code: (draft.code ?? '').trim(),
        name: (draft.name ?? '').trim(),
        category: (draft.category ?? '').trim(),
        ...(draft.defaultPriority ? { defaultPriority: draft.defaultPriority } : {}),
        ...(draft.estDurationMin ? { estDurationMin: Number(draft.estDurationMin) } : {}),
        /*
         * '0' and 'false' are truthy as strings, which is what lets a rate be
         * cleared to zero or switched back to per-job and have the change reach
         * the server rather than read as untouched.
         */
        ...(draft.allowanceKes ? { allowanceKes: Number(draft.allowanceKes) } : {}),
        ...(draft.allowancePerUnit ? { allowancePerUnit: draft.allowancePerUnit === 'true' } : {}),
        allowanceUnitKey: (draft.allowanceUnitKey ?? '').trim(),
        allowanceUnitLabel: (draft.allowanceUnitLabel ?? '').trim(),
        ...(draft.allowanceUnitDivisor
          ? { allowanceUnitDivisor: Number(draft.allowanceUnitDivisor) }
          : {}),
        ...(draft.allowanceDailyCap ? { allowanceDailyCap: Number(draft.allowanceDailyCap) } : {}),
      }),
    onSuccess: (row) => said(`${row.name} is on the list.`, refreshJobTypes),
    onError: (error) => failed(error, 'Could not add that job type.'),
  });

  const editJobType = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: Record<string, string> }) =>
      api.editJobType(id, {
        name: (draft.name ?? '').trim(),
        category: (draft.category ?? '').trim(),
        ...(draft.defaultPriority ? { defaultPriority: draft.defaultPriority } : {}),
        ...(draft.estDurationMin ? { estDurationMin: Number(draft.estDurationMin) } : {}),
        /*
         * '0' and 'false' are truthy as strings, which is what lets a rate be
         * cleared to zero or switched back to per-job and have the change reach
         * the server rather than read as untouched.
         */
        ...(draft.allowanceKes ? { allowanceKes: Number(draft.allowanceKes) } : {}),
        ...(draft.allowancePerUnit ? { allowancePerUnit: draft.allowancePerUnit === 'true' } : {}),
        allowanceUnitKey: (draft.allowanceUnitKey ?? '').trim(),
        allowanceUnitLabel: (draft.allowanceUnitLabel ?? '').trim(),
        ...(draft.allowanceUnitDivisor
          ? { allowanceUnitDivisor: Number(draft.allowanceUnitDivisor) }
          : {}),
        ...(draft.allowanceDailyCap ? { allowanceDailyCap: Number(draft.allowanceDailyCap) } : {}),
      }),
    onSuccess: (row) => said(`${row.name} saved. No ticket was re-filed.`, refreshJobTypes),
    onError: (error) => failed(error, 'Could not save that job type.'),
  });

  const retireJobType = useMutation({
    mutationFn: (id: string) => api.retireJobType(id),
    onSuccess: (result) => said(result.message, refreshJobTypes),
    onError: (error) => failed(error, 'Could not remove that job type.'),
  });

  const restoreJobType = useMutation({
    mutationFn: (id: string) => api.editJobType(id, { active: true }),
    onSuccess: (row) => said(`${row.name} is back on the list.`, refreshJobTypes),
    onError: (error) => failed(error, 'Could not restore that job type.'),
  });

  const JOB_TYPE_FIELDS: Array<ManagedField<JobTypeRow>> = [
    {
      name: 'code',
      label: 'Short code',
      required: true,
      addOnly: true,
      placeholder: 'SURVEY',
      hint: 'Capitals and underscores. Fixed once saved.',
      help: (
        <>
          What every report groups on. It is stored rather than the name, which is why it cannot be
          changed afterwards and why the name can.
          <br />
          <br />
          Keep it short and recognisable a year later in a spreadsheet column — SURVEY, NEW_INSTALL,
          FAULT_FIBRE.
        </>
      ),
    },
    {
      name: 'name',
      label: 'Name',
      required: true,
      placeholder: 'Site survey',
      hint: 'What a dispatcher picks on a ticket.',
      help: <>The full name, as somebody raising a ticket would look for it. Safe to correct at any time — tickets hold the id, not the name.</>,
    },
    {
      name: 'category',
      label: 'Category',
      required: true,
      placeholder: 'survey',
      hint: 'How job types are grouped in the picker.',
      show: (row) => row.category,
    },
    {
      name: 'defaultPriority',
      label: 'Default priority',
      type: 'select',
      options: ['P1', 'P2', 'P3', 'P4'].map((value) => ({ value, label: value })),
      hint: 'What the SLA clock starts on.',
      help: (
        <>
          The priority a new ticket of this kind starts at. It can be changed on the ticket, but
          this is what it gets if nobody thinks about it — so set it to what this work usually
          actually is, not to what would be nice.
          <br />
          <br />
          P1 pages people. Everything marked urgent means nothing is.
        </>
      ),
      show: (row) => row.defaultPriority,
    },
    {
      name: 'estDurationMin',
      label: 'Usual minutes on site',
      type: 'number',
      hint: 'What a technician’s day is planned against.',
      show: (row) => `${row.estDurationMin} min`,
    },
    {
      name: 'allowanceKes',
      label: 'Field allowance (KES)',
      type: 'number',
      placeholder: '160',
      hint: 'What a technician earns for one. Leave at 0 where the work is salaried.',
      help: (
        <>
          This is what somebody is paid, so it lives on the job type rather than on each contract
          — two technicians doing the same connection earn the same, and a rate kept per
          contract is a dozen places to change when it moves.
          <br />
          <br />
          Changing it is recorded in the audit log with the old figure and the new. That entry is
          the only answer to &ldquo;why did September pay less than August&rdquo;.
        </>
      ),
      show: (row) =>
        row.allowanceKes > 0
          ? `KES ${row.allowanceKes.toLocaleString('en-KE')}${
              row.allowancePerUnit ? ` per ${row.allowanceUnitLabel ?? 'unit'}` : ' each'
            }${row.allowanceDailyCap ? ` · max ${row.allowanceDailyCap}/day` : ''}`
          : null,
    },
    {
      name: 'allowancePerUnit',
      label: 'Paid by',
      type: 'select',
      options: [
        { value: 'false', label: 'The job — paid once, however long it took' },
        { value: 'true', label: 'A quantity — per kilometre, per pole' },
      ],
      hint: 'A connection is paid once. A cable run is paid by the kilometre.',
    },
    {
      name: 'allowanceUnitKey',
      label: 'Quantity field',
      placeholder: 'kilometres',
      hint: 'Which completion-form field holds the number. Only for per-quantity work.',
      help: (
        <>
          It must match the field name on this job type&rsquo;s completion form exactly. If it does
          not, every one of these jobs is listed as waiting on a quantity and none of them pays
          — which is loud on purpose, because the alternative is paying zero and looking
          settled.
        </>
      ),
    },
    {
      name: 'allowanceUnitLabel',
      label: 'Quantity called',
      placeholder: 'kilometres',
      hint: 'What that number is called on the statement.',
    },
    {
      name: 'allowanceUnitDivisor',
      label: 'Recorded units per paid unit',
      type: 'number',
      placeholder: '1',
      hint: 'Leave at 1 unless the form records a different unit from the one you pay by.',
      help: (
        <>
          The completion form records what is useful on site, which is not always what the contract
          pays by. An aerial run is measured in metres, because that is what a technician paces —
          and the allowance is per kilometre. That is 1000 here.
          <br />
          <br />
          Getting this wrong is expensive in one direction only: a 1,350 metre run with this left at
          1 would pay the per-kilometre rate 1,350 times.
        </>
      ),
    },
    {
      name: 'allowanceDailyCap',
      label: 'Most paid in a day',
      type: 'number',
      placeholder: '4',
      hint: 'Leave empty for no limit.',
      help: (
        <>
          Four connections is a day&rsquo;s work; fourteen means something was counted that was not
          done. Where the cap bites, the earliest jobs of that day are the ones paid — so the
          excluded ones are the ones at the end, which is the version that can be explained to the
          person whose pay it reduces.
        </>
      ),
    },
  ];

  /* ── Zones ───────────────────────────────────────────────────────── */

  const mayZones = can('area:write');
  const zones = useQuery({ queryKey: ['zones-all'], queryFn: api.zonesAll });
  const refreshZones = ['zones-all', 'zones', 'areas'];

  const addZone = useMutation({
    mutationFn: (draft: Record<string, string>) =>
      api.addZone({
        code: (draft.code ?? '').trim(),
        name: (draft.name ?? '').trim(),
        ...(draft.travelMin ? { defaultTravelMin: Number(draft.travelMin) } : {}),
      }),
    onSuccess: (row) => said(`${row.name} is on the list.`, refreshZones),
    onError: (error) => failed(error, 'Could not add that zone.'),
  });

  const editZone = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: Record<string, string> }) =>
      api.editZone(id, {
        name: (draft.name ?? '').trim(),
        ...(draft.travelMin ? { defaultTravelMin: Number(draft.travelMin) } : {}),
      }),
    onSuccess: (row) => said(`${row.name} saved.`, refreshZones),
    onError: (error) => failed(error, 'Could not save that zone.'),
  });

  const retireZone = useMutation({
    mutationFn: (id: string) => api.retireZone(id),
    onSuccess: (result) => said(result.message, refreshZones),
    onError: (error) => failed(error, 'Could not retire that zone.'),
  });

  const restoreZone = useMutation({
    mutationFn: (id: string) => api.editZone(id, { active: true }),
    onSuccess: (row) => said(`${row.name} is back on the list.`, refreshZones),
    onError: (error) => failed(error, 'Could not restore that zone.'),
  });

  const ZONE_FIELDS: Array<ManagedField<ZoneRow>> = [
    {
      name: 'code',
      label: 'Code',
      required: true,
      addOnly: true,
      placeholder: 'NYA',
      hint: 'A few letters. Fixed once saved.',
      help: <>Short, and the thing reports group on. Two or three letters is usual — NYA, TWN, KER.</>,
    },
    {
      name: 'name',
      label: 'Name',
      required: true,
      placeholder: 'Nyanchwa',
      hint: 'The part of town this covers.',
    },
    {
      name: 'travelMin',
      label: 'Usual minutes from town',
      type: 'number',
      hint: 'Used when an area has no time of its own.',
      help: (
        <>
          How long it usually takes to get out here. Dispatch reasons about this when nothing more
          specific is known, so an honest number beats an optimistic one — an optimistic one turns
          into a breached SLA rather than a faster technician.
        </>
      ),
      show: (row) => `${row.travelMin} min from town`,
    },
  ];

  /* ── Suppliers ───────────────────────────────────────────────────── */

  const maySuppliers = can('supplier:write');
  const suppliers = useQuery({ queryKey: ['suppliers-all'], queryFn: api.suppliersAll });
  const refreshSuppliers = ['suppliers-all', 'suppliers'];

  const editSupplier = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: Record<string, string> }) =>
      api.editSupplier(id, {
        name: (draft.name ?? '').trim(),
        contactName: (draft.contactName ?? '').trim(),
        phoneE164: (draft.phoneE164 ?? '').trim(),
        email: (draft.email ?? '').trim(),
        kraPin: (draft.kraPin ?? '').trim(),
        ...(draft.paymentTermDays ? { paymentTermDays: Number(draft.paymentTermDays) } : {}),
      }),
    onSuccess: (row) => said(`${row.name} saved.`, refreshSuppliers),
    onError: (error) => failed(error, 'Could not save that supplier.'),
  });

  const retireSupplier = useMutation({
    mutationFn: (id: string) => api.retireSupplier(id),
    onSuccess: (result) => said(result.message, refreshSuppliers),
    onError: (error) => failed(error, 'Could not remove that supplier.'),
  });

  const restoreSupplier = useMutation({
    mutationFn: (id: string) => api.editSupplier(id, { isActive: true }),
    onSuccess: (row) => said(`${row.name} is back on the list.`, refreshSuppliers),
    onError: (error) => failed(error, 'Could not restore that supplier.'),
  });

  const SUPPLIER_FIELDS: Array<ManagedField<SupplierRow>> = [
    { name: 'name', label: 'Name', required: true, hint: 'As it appears on their invoice.' },
    { name: 'contactName', label: 'Who you deal with', show: (row) => row.contactName },
    { name: 'phoneE164', label: 'Telephone', placeholder: '+2547…', show: (row) => row.phoneE164 },
    { name: 'email', label: 'Email', show: (row) => row.email },
    {
      name: 'kraPin',
      label: 'KRA PIN',
      hint: 'Needed on their invoice to reclaim input VAT.',
      help: (
        <>
          The supplier’s KRA PIN. Without it on the purchase invoice the input VAT on everything
          you buy from them cannot be reclaimed, which is real money.
          <br />
          <br />
          Stored in capitals whatever you type.
        </>
      ),
      show: (row) => row.kraPin,
    },
    {
      name: 'paymentTermDays',
      label: 'Payment terms (days)',
      type: 'number',
      hint: 'Days from delivery to payment, as agreed. Drives the ageing.',
      show: (row) => `${row.paymentTermDays} day terms`,
    },
  ];

  /* ── Stores and vans ─────────────────────────────────────────────── */

  const mayStock = can('stock:manage');
  const locations = useQuery({ queryKey: ['stock-locations-all'], queryFn: api.stockLocationsAll });
  const refreshLocations = ['stock-locations-all', 'stock-locations', 'stock-overview'];

  const editLocation = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: Record<string, string> }) =>
      api.editStockLocation(id, { name: (draft.name ?? '').trim() }),
    onSuccess: (row) => said(`${row.name} saved. Every movement it holds is untouched.`, refreshLocations),
    onError: (error) => failed(error, 'Could not rename that.'),
  });

  const retireLocation = useMutation({
    mutationFn: (id: string) => api.retireStockLocation(id),
    onSuccess: (result) => said(result.message, refreshLocations),
    onError: (error) => failed(error, 'Could not retire that.'),
  });

  const restoreLocation = useMutation({
    mutationFn: (id: string) => api.editStockLocation(id, { isActive: true }),
    onSuccess: (row) => said(`${row.name} is back in use.`, refreshLocations),
    onError: (error) => failed(error, 'Could not restore that.'),
  });

  const LOCATION_FIELDS: Array<ManagedField<StockLocationRow>> = [
    {
      name: 'name',
      label: 'Name',
      required: true,
      hint: 'Safe to change — movements point at the id, not the name.',
      help: (
        <>
          Renaming a store or a van re-labels nothing: every movement ever recorded against it
          points at the row, not the words.
          <br />
          <br />
          Worth doing when a van still carries the name of somebody who left.
        </>
      ),
      show: (row) => (row.technicianName ? `${row.kind} · ${row.technicianName}` : row.kind),
    },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="flex items-center gap-1.5 font-heading text-2xl">
          <ListTree className="h-6 w-6 text-primary-600" aria-hidden="true" />
          Lists
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          The reference data every other screen is built from. Nothing here is deleted once
          something has used it — it is retired, and comes back whenever you like.
        </p>
      </div>

      <ManagedList<JobTypeRow>
        testId="job-types"
        title="Job types"
        blurb="What kinds of work you do"
        help={
          <>
            Every ticket is one of these, and they decide two measured things: the priority the SLA
            clock starts on, and how long a technician’s day is planned against.
            <br />
            <br />
            Retiring one stops it being offered on new tickets and leaves every ticket already
            raised under it exactly as it is.
          </>
        }
        fields={JOB_TYPE_FIELDS}
        rows={jobTypes.data?.jobTypes ?? []}
        loading={jobTypes.isPending}
        error={jobTypes.isError}
        nameOf={(row) => row.name}
        keyOf={(row) => row.code}
        usageOf={(row) => ({ count: row.ticketCount, noun: 'ticket' })}
        retiredOf={(row) => !row.active}
        mayEdit={mayJobTypes}
        mayRetire={mayJobTypes}
        onAdd={(draft) => addJobType.mutateAsync(draft).catch(() => undefined)}
        onEdit={(id, draft) => editJobType.mutateAsync({ id, draft }).catch(() => undefined)}
        onRetire={(id) => retireJobType.mutate(id)}
        onRestore={(id) => restoreJobType.mutate(id)}
        pending={addJobType.isPending || editJobType.isPending || retireJobType.isPending}
        outcome={outcome}
        problem={problem}
        onDismiss={() => setOutcome(null)}
        onProblem={setProblem}
        addLabel="Add a job type"
      />

      <ManagedList<ZoneRow>
        testId="zones"
        title="Dispatch zones"
        blurb="The coarse grouping every area hangs off"
        help={
          <>
            Areas belong to zones, and a zone’s travel time is what dispatch uses when an area has
            none of its own.
            <br />
            <br />
            A zone still holding live areas cannot be retired — an area whose zone is gone is one
            nobody can dispatch against. Move or retire its areas first.
          </>
        }
        fields={ZONE_FIELDS}
        rows={zones.data?.zones ?? []}
        loading={zones.isPending}
        error={zones.isError}
        nameOf={(row) => row.name}
        keyOf={(row) => row.code}
        usageOf={(row) => ({ count: row.areaCount, noun: 'area' })}
        retiredOf={(row) => !row.active}
        mayEdit={mayZones}
        mayRetire={mayZones}
        onAdd={(draft) => addZone.mutateAsync(draft).catch(() => undefined)}
        onEdit={(id, draft) => editZone.mutateAsync({ id, draft }).catch(() => undefined)}
        onRetire={(id) => retireZone.mutate(id)}
        onRestore={(id) => restoreZone.mutate(id)}
        pending={addZone.isPending || editZone.isPending || retireZone.isPending}
        outcome={outcome}
        problem={problem}
        onDismiss={() => setOutcome(null)}
        onProblem={setProblem}
        addLabel="Add a zone"
        collapsible
      />

      <ManagedList<SupplierRow>
        testId="suppliers"
        title="Suppliers"
        blurb="Who you buy from"
        help={
          <>
            Added from the Purchasing screen when you raise your first order with somebody. This is
            where they are corrected — a KRA PIN typed wrongly is an input VAT claim your accountant
            cannot make, and until now there was no way to fix it.
            <br />
            <br />
            A supplier you have ordered from is retired rather than deleted, so the purchase ledger
            keeps saying who each order was with.
          </>
        }
        fields={SUPPLIER_FIELDS}
        rows={suppliers.data ?? []}
        loading={suppliers.isPending}
        error={suppliers.isError}
        nameOf={(row) => row.name}
        usageOf={(row) => ({ count: row.orderCount ?? 0, noun: 'order' })}
        retiredOf={(row) => row.isActive === false}
        mayEdit={maySuppliers}
        mayRetire={maySuppliers}
        onAdd={() => setProblem('Suppliers are added from the Purchasing screen.')}
        onEdit={(id, draft) => editSupplier.mutateAsync({ id, draft }).catch(() => undefined)}
        onRetire={(id) => retireSupplier.mutate(id)}
        onRestore={(id) => restoreSupplier.mutate(id)}
        pending={editSupplier.isPending || retireSupplier.isPending}
        outcome={outcome}
        problem={problem}
        onDismiss={() => setOutcome(null)}
        onProblem={setProblem}
        collapsible
      />

      <ManagedList<StockLocationRow>
        testId="locations"
        title="Stores and vans"
        blurb="Where stock stands"
        help={
          <>
            Created by <strong>Set up the vans</strong> on the stock screen, one per technician, so
            there is nothing to add here.
            <br />
            <br />
            Renaming is safe. Retiring one that still holds stock is refused and says what is
            standing in it — retiring it would not remove the stock, it would leave every figure
            counting things nobody can see.
          </>
        }
        fields={LOCATION_FIELDS}
        rows={locations.data ?? []}
        loading={locations.isPending}
        error={locations.isError}
        nameOf={(row) => row.name}
        retiredOf={(row) => row.isActive === false}
        mayEdit={mayStock}
        mayRetire={mayStock}
        /* Vans are made by the setup action, which knows the roster. */
        onAdd={() => setProblem('Stores and vans are created by “Set up the vans” on the stock screen.')}
        onEdit={(id, draft) => editLocation.mutateAsync({ id, draft }).catch(() => undefined)}
        onRetire={(id) => retireLocation.mutate(id)}
        onRestore={(id) => restoreLocation.mutate(id)}
        pending={editLocation.isPending || retireLocation.isPending}
        outcome={outcome}
        problem={problem}
        onDismiss={() => setOutcome(null)}
        onProblem={setProblem}
        addLabel="Add one"
        collapsible
      />
    </div>
  );
}
